<script setup lang="ts">
/**
 * Libreta de clientes del provider (§20), con el formato del listado de
 * usuarios de la plantilla (`pages/apps/user/list/index.vue`) y el resumen
 * compacto del listado de pedidos: una tarjeta de métricas con divisores,
 * filtros y buscador en la misma línea, y `VDataTableServer` con avatar, chips
 * de estado y acciones por fila.
 *
 * Vive al nivel de Dashboard y no dentro de un dive center: la libreta es de la
 * CUENTA. Un manager solo ve las fichas que pasaron por sus centros asignados,
 * pero eso lo resuelve el backend — acá no se filtra por rol.
 */
import { listDiveCenters } from '@/api/dive-centers'
import {
  type ClientPayload,
  createClient,
  deleteClientDocument,
  listClients,
  uploadClientDocument,
} from '@/api/clients'
import { createBlankMedicalFormPdfLink, submitClientMedicalForm } from '@/api/medical'

// Los nueve idiomas de la app, la misma lista que el formulario público del QR.
import { INTAKE_LOCALES } from '@/data/intakeI18n'
import { ApiError, apiOrigin } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ClientEditorModal, { type ClientEditorExtras } from '@/components/ClientEditorModal.vue'
import RequestClientLinkDialog from '@/components/RequestClientLinkDialog.vue'
import InviteClientQrDialog from '@/components/InviteClientQrDialog.vue'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import type { ClientLinkStatus, DiveCenter, DiveCenterClient } from '@/types'

definePage({ meta: { requiresAuth: true } })

const loading = ref(true)
const error = ref('')

const clients = ref<DiveCenterClient[]>([])
const total = ref(0)

// Opciones de la tabla, con los nombres que espera `VDataTableServer`.
const itemsPerPage = ref(10)
const page = ref(1)

const diveCenters = ref<DiveCenter[]>([])

const search = ref('')
const linkFilter = ref<ClientLinkStatus | null>(null)
const centerFilter = ref<string | null>(null)

const route = useRoute()
const router = useRouter()

const showEditor = ref(false)
const editing = ref<DiveCenterClient | null>(null)
const submitting = ref(false)
const editorError = ref('')

const blankPdfLoading = ref(false)
const showQrDialog = ref(false)
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const headers = [
  { title: 'Client', key: 'client' },
  { title: 'Certification', key: 'certification', sortable: false },
  { title: 'Medical form', key: 'medical', sortable: false },
  { title: 'App', key: 'link', sortable: false },
  { title: '', key: 'actions', sortable: false, width: 64 },
]

const linkStatuses = [
  { title: 'Linked', value: 'linked' },
  { title: 'Awaiting response', value: 'pending' },
  { title: 'Not linked', value: 'none' },
  { title: 'Rejected', value: 'rejected' },
  { title: 'Revoked', value: 'revoked' },
]

const centerItems = computed(() =>
  diveCenters.value.map(dc => ({ title: dc.name, value: dc.id })),
)

/*
 * Contadores del resumen.
 *
 * Salen de tres consultas con `limit: 1` que solo leen el `total` — el endpoint
 * ya sabe contar por `linkStatus`, así que es más barato que traerse la libreta
 * entera para contarla en el navegador. Sin porcentajes de variación como en la
 * demo de la plantilla: no hay serie histórica de dónde sacarlos, y un número
 * inventado en un panel de gestión es peor que no tenerlo.
 */
const counts = ref({ total: 0, linked: 0, pending: 0 })

const widgets = computed(() => [
  {
    title: 'Clients',
    value: counts.value.total,
    icon: 'ri-group-line',
  },
  {
    title: 'Linked',
    value: counts.value.linked,
    icon: 'ri-user-follow-line',
  },
  {
    title: 'Invited',
    value: counts.value.pending,
    icon: 'ri-user-search-line',
  },
  {
    title: 'Not linked',
    value: Math.max(0, counts.value.total - counts.value.linked - counts.value.pending),
    icon: 'ri-user-line',
  },
])

const showLinkRequest = ref(false)

const openLinkRequest = () => {
  showLinkRequest.value = true
}

/**
 * Sin match: se abre el alta de cliente de siempre, precargando el mail si eso
 * fue lo que se escribió. Un nickname o un link no son datos de la ficha, así
 * que esos no se precargan — pero el centro ya los tiene a mano.
 */
const prefillEmail = ref('')

/** Abre la ficha de alguien que ya era cliente, desde el diálogo de búsqueda. */
const openExistingClient = (clientId: string) => {
  router.push(`/clients/${clientId}`)
}

const startCreateFrom = (payload: { mode: string; value: string }) => {
  startCreate()
  if (payload.mode === 'email')
    prefillEmail.value = payload.value
}

/*
 * `pending` dice "awaiting response" y ya no "invited": aquello era el mail de
 * recomendación (`invitedAt`), que es otra cosa y sigue existiendo.
 *
 * `rejected` y `revoked` se muestran por separado a propósito: nunca aceptó vs.
 * aceptó y se arrepintió. Al primero tiene sentido volver a pedirle; al segundo
 * hay que preguntarle antes.
 */
const linkChip = (status?: ClientLinkStatus) => {
  if (status === 'linked')
    return { color: 'success', label: 'linked' }
  if (status === 'pending')
    return { color: 'warning', label: 'awaiting response' }
  if (status === 'rejected')
    return { color: 'error', label: 'rejected' }
  if (status === 'revoked')
    return { color: 'error', label: 'revoked' }

  return { color: 'secondary', label: 'not linked' }
}

/**
 * Con vínculo aceptado manda el nombre del buzo; sin él, lo que cargó el centro.
 *
 * Una ficha creada desde el mostrador nace con un nombre provisorio y se queda
 * con él hasta que la persona acepta — recién ahí el backend manda `linkedProfile`.
 */
const fullName = (c: DiveCenterClient) => {
  const diver = c.linkedProfile?.diver

  if (diver?.firstName || diver?.nickName)
    return [diver.firstName, diver.nickName ? `@${diver.nickName}` : ''].filter(Boolean).join(' ').trim()

  return `${c.firstName} ${c.lastName ?? ''}`.trim()
}

/*
 * Qué disparó el aviso de duplicado.
 *
 * El backend manda el motivo justamente para poder decirlo: sin él, "posible
 * duplicado" es una acusación sin evidencia y lo único que se puede hacer es
 * recorrer la libreta a ojo.
 */
const duplicateHint = (c: DiveCenterClient) =>
  c.duplicateReason === 'identity'
    ? 'Another client has the same name and date of birth.'
    : 'Another client has the same email address.'

/**
 * Estado del cuestionario médico.
 *
 * `draft` es la declaración que cargó el centro sin que nadie la firmara: existe
 * pero no vale como declaración, así que se lee distinto de una firmada — y
 * distinto de no tener nada, porque hay algo que imprimir.
 */
const medicalChip = (c: DiveCenterClient) => {
  if (c.medicalStatus === 'signed')
    return { color: 'success', label: 'Completed' }
  if (c.medicalStatus === 'expired')
    return { color: 'warning', label: 'Expired' }
  if (c.medicalStatus === 'draft')
    return { color: 'info', label: 'Unsigned' }

  return { color: 'secondary', label: 'Missing' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await listClients({
      search: search.value,
      linkStatus: linkFilter.value || undefined,
      diveCenterId: centerFilter.value || undefined,
      limit: itemsPerPage.value,
      skip: (page.value - 1) * itemsPerPage.value,
    })

    clients.value = res.clients
    total.value = res.total
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not load clients.'
  }
  finally {
    loading.value = false
  }
}

async function loadCounts() {
  try {
    const [all, linked, pending] = await Promise.all([
      listClients({ limit: 1 }),
      listClients({ linkStatus: 'linked', limit: 1 }),
      listClients({ linkStatus: 'pending', limit: 1 }),
    ])

    counts.value = { total: all.total, linked: linked.total, pending: pending.total }
  }
  catch { /* los widgets son contexto: el listado funciona igual sin ellos */ }
}

onMounted(async () => {
  // Los centros se piden una sola vez: alimentan el picker del editor, el
  // filtro y los nombres del listado, y no cambian mientras se navega.
  try {
    const d = await listDiveCenters()

    diveCenters.value = d.diveCenters
  }
  catch { /* el listado funciona igual sin los nombres */ }

  await Promise.all([load(), loadCounts()])

  /*
   * `?edit=<id>` y `?activities=<id>` son las puertas viejas —del roster y del
   * dashboard de ventas— desde cuando la ficha era un modal. Ahora hay una
   * pantalla propia, así que se redirige en vez de duplicar el camino: los dos
   * enlaces siguen funcionando y terminan donde está todo.
   */
  const target = String(route.query.activities ?? route.query.edit ?? '')

  if (target)
    router.replace(`/clients/${target}${route.query.edit ? '?edit=1' : ''}`)
})

/*
 * Buscador con debounce: cada tecla dispararía una request y la respuesta lenta
 * de una consulta vieja podría pisar a la nueva. 350 ms es el punto donde deja
 * de sentirse el retraso y ya no se manda una request por letra.
 */
let searchTimer: ReturnType<typeof setTimeout> | undefined

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.value = 1
    load()
  }, 350)
})

watch([page, itemsPerPage, linkFilter, centerFilter], (next, prev) => {
  // Cambiar un filtro o el tamaño de página vuelve a la primera: quedarse en la
  // 3 de un resultado que ahora tiene una sola muestra la tabla vacía.
  const filtersChanged = next[1] !== prev[1] || next[2] !== prev[2] || next[3] !== prev[3]

  if (filtersChanged && page.value !== 1) {
    page.value = 1

    return
  }
  load()
})

function startCreate() {
  editing.value = null
  editorError.value = ''
  prefillEmail.value = ''
  showEditor.value = true
}

async function submitEditor(payload: ClientPayload, extras: ClientEditorExtras) {
  editorError.value = ''
  submitting.value = true
  try {
    // Solo alta: editar vive en la ficha, que es donde está el resto (§56).
    const saved = (await createClient(payload)).client

    notifySuccess(`${payload.firstName} ${payload.lastName} added.`)

    /*
     * La declaración y los archivos van DESPUÉS del alta, porque cuelgan de un
     * `clientId` que hasta acá no existía. Si alguno falla la ficha ya quedó
     * creada: se avisa qué no entró en vez de deshacer el alta, que es peor
     * (mismo criterio que las reservas al crear una actividad, §20.10).
     */
    if (extras.medical) {
      try {
        await submitClientMedicalForm(saved.id, extras.medical)
      }
      catch (e) {
        notifyError(apiMessage(e, 'The client was saved, but the medical form was not.'))
      }
    }

    if (extras.documents.length) {
      // En serie y no en paralelo: el backend valida el tope contra el estado
      // actual, y en paralelo dos subidas leerían el mismo conteo y se pasarían.
      for (const file of extras.documents) {
        try {
          await uploadClientDocument(saved.id, file)
        }
        catch (e) {
          notifyError(apiMessage(e, `Could not upload ${file.name}.`))
        }
      }
    }

    showEditor.value = false
    await Promise.all([load(), loadCounts()])
  }
  catch (e) {
    editorError.value = e instanceof ApiError ? e.message : 'Could not save the client.'
  }
  finally {
    submitting.value = false
  }
}

/**
 * Adjuntos de una ficha que ya existe: se suben en el acto, sin esperar al
 * Save. `editing` se reemplaza con la respuesta para que la lista del paso se
 * actualice sin cerrar el modal.
 */
async function uploadDocument(file: File) {
  if (!editing.value)
    return

  try {
    const res = await uploadClientDocument(editing.value.id, file)

    editing.value = res.client
  }
  catch (e) {
    notifyError(apiMessage(e, `Could not upload ${file.name}.`))
  }
}

async function removeDocument(index: number) {
  if (!editing.value)
    return

  try {
    const res = await deleteClientDocument(editing.value.id, index)

    editing.value = res.client
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not remove the document.'))
  }
}

/**
 * La hoja en limpio de la cuenta: las preguntas activas y nada más, para dejar
 * en el mostrador. No cuelga de ninguna ficha —antes había que abrir el menú de
 * un cliente cualquiera para conseguirla, que es pedir el documento de una
 * persona para imprimir uno que no es de nadie.
 */
async function openBlankMedicalPdf(locale: string) {
  const tab = window.open('', '_blank')

  blankPdfLoading.value = true
  try {
    const { url } = await createBlankMedicalFormPdfLink(locale)
    const absolute = new URL(url, apiOrigin()).toString()

    if (tab)
      tab.location.href = absolute
    else
      window.open(absolute, '_blank')
  }
  catch (e) {
    tab?.close()
    notifyError(apiMessage(e, 'Could not open the form.'))
  }
  finally {
    blankPdfLoading.value = false
  }
}
</script>

<template>
  <section>
    <!--
      Resumen en UNA tarjeta con divisores, como el listado de pedidos de la
      plantilla (`pages/apps/ecommerce/order/list`). Cuatro tarjetas sueltas
      ocupaban una pantalla entera para cuatro números. El avatar va sin color:
      el gris de la plantilla alcanza y no compite con los chips de la tabla.
    -->
    <VCard class="mb-6">
      <VCardText class="px-2">
        <VRow>
          <template
            v-for="(w, index) in widgets"
            :key="w.title"
          >
            <VCol
              cols="12"
              sm="6"
              md="3"
              class="px-6"
            >
              <div
                class="d-flex justify-space-between"
                :class="$vuetify.display.xs
                  ? index !== widgets.length - 1 ? 'border-b pb-4' : ''
                  : $vuetify.display.sm
                    ? index < (widgets.length / 2) ? 'border-b pb-4' : ''
                    : ''"
              >
                <div class="d-flex flex-column gap-y-1">
                  <h4 class="text-h4">
                    {{ w.value }}
                  </h4>
                  <span class="text-base">{{ w.title }}</span>
                </div>

                <VAvatar
                  variant="tonal"
                  rounded
                  size="42"
                >
                  <VIcon
                    :icon="w.icon"
                    size="26"
                  />
                </VAvatar>
              </div>
            </VCol>
            <VDivider
              v-if="$vuetify.display.mdAndUp ? index !== widgets.length - 1
                : $vuetify.display.smAndUp ? index % 2 === 0
                  : false"
              vertical
              inset
              length="60"
            />
          </template>
        </VRow>
      </VCardText>
    </VCard>

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard class="mb-6">
      <!--
        Filtros y buscador en una sola línea. Antes eran dos bloques con su
        divisor: media pantalla para dos desplegables y un campo de texto.
      -->
      <VCardText class="d-flex flex-wrap align-center gap-4">
        <VSelect
          v-model="linkFilter"
          label="Blue Trails account"
          placeholder="Any"
          :items="linkStatuses"
          density="compact"
          clearable
          clear-icon="ri-close-line"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />
        <VSelect
          v-model="centerFilter"
          label="Dive center"
          placeholder="Any"
          :items="centerItems"
          density="compact"
          clearable
          clear-icon="ri-close-line"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />

        <VSpacer />

        <VTextField
          v-model="search"
          placeholder="Search clients"
          density="compact"
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />
        <!--
          Las tres formas de sumar a alguien, bajo un solo botón: eran tres
          botones sueltos y con los dos filtros y el buscador en la misma fila
          el último caía al renglón de abajo.

          El orden es por frecuencia real: cargar a mano es lo habitual, buscar
          en Blue Trails es lo nuevo, y el QR es para dejarlo en el mostrador.
        -->
        <!--
          La hoja en limpio, al lado del alta. Vivía en el menú de cada fila, que
          obligaba a abrir la ficha de alguien para imprimir un papel que no es
          de nadie: son las preguntas activas y nada más, para dejar en el
          mostrador. Va como botón suelto porque no depende de ninguna selección.
        -->
        <VBtn
          variant="tonal"
          prepend-icon="ri-printer-line"
          append-icon="ri-arrow-down-s-line"
          :loading="blankPdfLoading"
        >
          Blank medical form

          <!--
            Los nueve idiomas acá y solo dos en la declaración ya firmada: este
            papel todavía no lo leyó nadie, así que se imprime en el idioma de
            quien lo va a completar. Aquella, en cambio, solo tiene sentido en
            el idioma en que se firmó o en inglés.
          -->
          <VMenu activator="parent">
            <VList
              density="compact"
              max-height="360"
            >
              <VListItem
                v-for="l in INTAKE_LOCALES"
                :key="l.code"
                @click="openBlankMedicalPdf(l.code)"
              >
                <VListItemTitle>{{ l.label }}</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
        </VBtn>

        <VBtn
          prepend-icon="ri-user-add-line"
          append-icon="ri-arrow-down-s-line"
        >
          Add Client

          <VMenu activator="parent">
            <VList>
              <VListItem @click="startCreate">
                <template #prepend>
                  <VIcon
                    icon="ri-user-add-line"
                    size="20"
                  />
                </template>
                <VListItemTitle>New client</VListItemTitle>
              </VListItem>

              <VListItem @click="openLinkRequest">
                <template #prepend>
                  <VIcon
                    icon="ri-link"
                    size="20"
                  />
                </template>
                <VListItemTitle>Add Blue Trails diver</VListItemTitle>
                <VListItemSubtitle>Look them up and send a request</VListItemSubtitle>
              </VListItem>

              <VListItem
                :disabled="!diveCenters.length"
                @click="showQrDialog = true"
              >
                <template #prepend>
                  <VIcon
                    icon="ri-qr-code-line"
                    size="20"
                  />
                </template>
                <VListItemTitle>Invite client</VListItemTitle>
                <VListItemSubtitle>QR for the counter</VListItemSubtitle>
              </VListItem>
            </VList>
          </VMenu>
        </VBtn>
      </VCardText>

      <!-- SECTION datatable -->
      <!--
        Sin casillas de selección: no hay ninguna acción por lote, y una columna
        de tildes que no lleva a nada invita a marcar cosas y después buscar el
        botón que no existe.
      -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="clients"
        item-value="id"
        :items-length="total"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap rounded-0"
      >
        <!-- Cliente -->
        <template #item.client="{ item }">
          <div class="d-flex align-center">
            <VAvatar
              size="34"
              variant="tonal"
              color="primary"
              class="me-3"
            >
              <span>{{ avatarText(fullName(item)) }}</span>
            </VAvatar>

            <div class="d-flex flex-column">
              <span class="text-base font-weight-medium">
                {{ fullName(item) }}
                <!--
                  El aviso dice POR QUÉ, no solo que sí. "Posible duplicado" a
                  secas obliga a adivinar contra qué se comparó y por dónde
                  empezar a buscar la otra ficha; con el motivo, resolverlo es
                  filtrar por ese mail o por ese nombre.
                -->
                <VChip
                  v-if="item.possibleDuplicate"
                  size="x-small"
                  color="warning"
                  class="ms-1"
                  :title="duplicateHint(item)"
                >
                  possible duplicate
                </VChip>
              </span>
              <span class="text-sm text-medium-emphasis">{{ item.email || item.phone || '—' }}</span>
            </div>
          </div>
        </template>

        <!-- Certificación -->
        <template #item.certification="{ item }">
          <span
            v-if="item.certificationLevel"
            class="text-high-emphasis"
          >
            {{ item.certificationLevel }}
            <span
              v-if="item.certificationAgency"
              class="text-medium-emphasis"
            >· {{ item.certificationAgency }}</span>
          </span>
          <span
            v-else
            class="text-disabled"
          >—</span>
        </template>

        <!-- Cuestionario médico -->
        <template #item.medical="{ item }">
          <VChip
            :color="medicalChip(item).color"
            size="small"
          >
            {{ medicalChip(item).label }}
          </VChip>
          <span
            v-if="item.medicalSubmittedAt"
            class="text-caption text-medium-emphasis ms-2"
          >{{ new Date(item.medicalSubmittedAt).toLocaleDateString() }}</span>
        </template>

        <!-- Vínculo con la app -->
        <template #item.link="{ item }">
          <VChip
            :color="linkChip(item.linkStatus).color"
            size="small"
            class="text-capitalize"
          >
            {{ linkChip(item.linkStatus).label }}
          </VChip>
        </template>

        <!--
          Una sola acción: abrir la ficha (§56). Editar, invitar, pedir el
          vínculo, ver las actividades y borrar viven ahí adentro, que es donde
          se ve contra qué se está decidiendo — el menú de la fila ofrecía cinco
          cosas sobre alguien de quien solo se veía el nombre.
        -->
        <template #item.actions="{ item }">
          <IconBtn
            size="small"
            :to="`/clients/${item.id}`"
          >
            <VIcon icon="ri-eye-line" />
          </IconBtn>
        </template>

        <template #no-data>
          <div class="text-center py-8">
            <p class="text-body-1 mb-4">
              Your own client book — divers you can book onto activities, whether or
              not they use Blue Trails.
            </p>
            <VBtn
              prepend-icon="ri-user-add-line"
              @click="startCreate"
            >
              Add your first client
            </VBtn>
          </div>
        </template>

        <!--
          Paginado del listado de pedidos de la plantilla: selector de filas,
          rango, y dos flechas sin borde. `TablePagination` de `@core` dibuja
          una barra de números que pesa más que la tabla en una libreta corta.
        -->
        <template #bottom>
          <VDivider />

          <div class="d-flex justify-end flex-wrap gap-x-6 px-2 py-1">
            <div class="d-flex align-center gap-x-2 text-medium-emphasis text-base">
              Rows Per Page:
              <VSelect
                v-model="itemsPerPage"
                class="per-page-select"
                variant="plain"
                :items="[10, 20, 25, 50, 100]"
              />
            </div>

            <p class="d-flex align-center text-base text-high-emphasis me-2 mb-0">
              {{ paginationMeta({ page, itemsPerPage }, total) }}
            </p>

            <div class="d-flex gap-x-2 align-center me-2">
              <VBtn
                class="flip-in-rtl"
                icon="ri-arrow-left-s-line"
                variant="text"
                density="comfortable"
                color="high-emphasis"
                :disabled="page <= 1"
                @click="page <= 1 ? page = 1 : page--"
              />

              <VBtn
                class="flip-in-rtl"
                icon="ri-arrow-right-s-line"
                density="comfortable"
                variant="text"
                color="high-emphasis"
                :disabled="page >= Math.ceil(total / itemsPerPage)"
                @click="page >= Math.ceil(total / itemsPerPage) ? page = Math.ceil(total / itemsPerPage) : page++"
              />
            </div>
          </div>
        </template>
      </VDataTableServer>
      <!-- !SECTION -->
    </VCard>

    <ClientEditorModal
      :show="showEditor"
      :client="editing"
      :dive-centers="diveCenters"
      :prefill-email="prefillEmail"
      :submitting="submitting"
      :error="editorError"
      @close="showEditor = false"
      @submit="submitEditor"
      @upload-document="uploadDocument"
      @remove-document="removeDocument"
    />

    <RequestClientLinkDialog
      :show="showLinkRequest"
      :client="null"
      :dive-centers="diveCenters"
      :dive-center-id="centerFilter"
      @close="showLinkRequest = false"
      @requested="load(); loadCounts()"
      @create-plain="startCreateFrom"
      @open-client="openExistingClient"
    />

    <InviteClientQrDialog
      :show="showQrDialog"
      :dive-centers="diveCenters"
      :dive-center-id="centerFilter"
      @close="showQrDialog = false"
    />

    <ConfirmDialog ref="confirmRef" />
  </section>
</template>
